import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api'
import { Card, Button, Alert, Badge, ProgressBar } from 'react-bootstrap';
import { formatDateTime } from '../utils/dateUtils';
import { useNavigate } from 'react-router-dom';

const QuizList = () => {
    const [quizzes, setQuizzes] = useState([]);
    const [groupedQuizzes, setGroupedQuizzes] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        fetchQuizzes();
    }, []);

    const fetchQuizzes = async () => {
        try {
            const response = await api.get('/api/quizzes');
            setQuizzes(response.data);
            
            // Group quizzes by study circle and folder
            const grouped = response.data.reduce((acc, quiz) => {
                const studyCircleName = quiz.study_circle_name || 'No Study Circle';
                const folderName = quiz.folder_name || 'No Folder';
                
                if (!acc[studyCircleName]) {
                    acc[studyCircleName] = {};
                }
                if (!acc[studyCircleName][folderName]) {
                    acc[studyCircleName][folderName] = [];
                }
                acc[studyCircleName][folderName].push(quiz);
                return acc;
            }, {});
            
            setGroupedQuizzes(grouped);
        } catch (err) {
            setError('Failed to fetch quizzes');
            console.error('Error fetching quizzes:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (quizId) => {
        if (!window.confirm('Are you sure you want to delete this quiz?')) {
            return;
        }

        try {
            await api.delete(`/api/quizzes/${quizId}`);
            fetchQuizzes();
        } catch (err) {
            setError('Failed to delete quiz');
            console.error('Error deleting quiz:', err);
        }
    };

    const renderAttemptInfo = (quiz) => {
        if (user.role !== 'student') return null;
        
        const maxAttempts = quiz.allowed_attempts;
        const attemptsLeft = maxAttempts - (quiz.attempts_made || 0);
        const progress = ((quiz.attempts_made || 0) / maxAttempts) * 100;
        
        return (
            <div className="mt-2">
                <small className="text-muted">
                    Attempts: {quiz.attempts_made || 0} / {maxAttempts}
                </small>
                <ProgressBar 
                    now={progress} 
                    variant={attemptsLeft > 0 ? "success" : "danger"}
                    className="mt-1"
                />
            </div>
        );
    };

    if (loading) {
        return <div className="text-center mt-5">Loading...</div>;
    }

    if (error) {
        return <div className="alert alert-danger mt-5">{error}</div>;
    }

    return (
        <div className="container mt-4">
            <h2 className="mb-4">Active Quizzes</h2>
            {error && <Alert variant="danger">{error}</Alert>}
            
            {(!quizzes || quizzes.length === 0) ? (
                <div className="text-center py-5">
                    <h3>No Quizzes Available</h3>
                    <p className="text-muted">There are currently no quizzes.</p>
                </div>
            ) : (
                <div>
                    {Object.entries(groupedQuizzes).map(([studyCircleName, folders], studyCircleIndex) => {
                        // Get the first quiz in the group to access study_circle_description
                        const firstQuiz = Object.values(folders)[0]?.[0];
                        return (
                            <div key={studyCircleIndex} className="mb-5">
                                <h3 className="mb-3">{studyCircleName}</h3>
                                {firstQuiz && firstQuiz.study_circle_description && (
                                    <div className="mb-3 text-muted" dangerouslySetInnerHTML={{ __html: firstQuiz.study_circle_description }} />
                                )}
                                {Object.entries(folders).map(([folderName, folderQuizzes], folderIndex) => (
                                    <div key={folderIndex} className="mb-4">
                                        <h5 className="mb-3 text-muted">{folderName}</h5>
                                        <div className="row">
                                            {folderQuizzes.map(quiz => (
                                                <div key={quiz.id} className="col-md-4 mb-3">
                                                    <Card>
                                                        <Card.Body>
                                                            <Card.Title>{quiz.title}</Card.Title>
                                                            <Card.Text as="div" dangerouslySetInnerHTML={{ __html: quiz.description }} />
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <small className="text-muted">
                                                                    Time Limit: {quiz.time_limit} minutes<br />
                                                                    Passing Score: {quiz.passing_score}%<br />
                                                                    Available: {formatDateTime(quiz.start_date, quiz.start_time)} - {formatDateTime(quiz.end_date, quiz.end_time)}
                                                                </small>
                                                            </div>
                                                            {renderAttemptInfo(quiz)}
                                                            <br />
                                                            {user.role === 'admin' && (
                                                                <Button 
                                                                    variant="primary" 
                                                                    onClick={() => navigate(`/quiz-editor/${quiz.id}`)}
                                                                >
                                                                    Edit Quiz
                                                                </Button>
                                                            )}

                                                            {user.role !== 'admin' && (
                                                                <Button 
                                                                    variant="primary" 
                                                                    onClick={() => navigate(`/quiz/${quiz.id}/attempt`)}
                                                                    hidden={quiz.attempts_made >= quiz.allowed_attempts}
                                                                >
                                                                    Take Quiz
                                                                </Button>
                                                            )}
                                                        </Card.Body>
                                                    </Card>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default QuizList; 